import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getTestSets, createTestSet, getProject } from '../services/api';

const TestSets = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  
  const [project, setProject] = useState(null);
  const [testSets, setTestSets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newTestSet, setNewTestSet] = useState({
    name: '',
    description: ''
  });

  useEffect(() => {
    loadData();
  }, [projectId]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [projectData, testSetsData] = await Promise.all([
        getProject(projectId),
        getTestSets(projectId)
      ]);
      setProject(projectData);
      setTestSets(testSetsData);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTestSet = async (e) => {
    e.preventDefault();
    try {
      const testSetData = {
        ...newTestSet,
        projectId
      };
      
      await createTestSet(testSetData);
      setNewTestSet({ name: '', description: '' });
      setShowCreateForm(false);
      loadData();
    } catch (error) {
      console.error('Error creating test set:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-celadon-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading test sets...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">

      {/* Page Header */}
      <div className="bg-white border-b border-gray-100 rounded-2xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
            <nav className="flex items-center space-x-2 text-sm text-gray-600">
            <button
              onClick={() => navigate(`/projects/${projectId}`)}
              className="flex items-center hover:text-celadon-600 transition-colors"
            >
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            
          </nav>
              {/* Icon */}
              <div className="bg-gradient-to-br from-celadon-50 to-celadon-100 p-4 rounded-2xl shadow-soft">
                <svg className="w-8 h-8 text-celadon-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                </svg>
              </div>
              
              {/* Title and Description */}
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Test Sets</h1>
                <p className="text-gray-600 mt-1">
                  Evaluate and compare prompt performance across different test scenarios
                </p>
                {testSets.length > 0 && (
                  <div className="flex items-center mt-3 text-sm text-gray-500">
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                    </svg>
                    {testSets.length} test set{testSets.length !== 1 ? 's' : ''} available
                  </div>
                )}
              </div>
            </div>
            
            {/* Action Button */}
            <button
              onClick={() => setShowCreateForm(true)}
              className="inline-flex items-center px-6 py-3 bg-celadon-600 text-white font-medium rounded-xl hover:bg-celadon-700 focus:outline-none focus:ring-2 focus:ring-celadon-500 focus:ring-offset-2 shadow-soft hover:shadow-medium transition-all duration-200 transform hover:scale-105"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              New Test Set
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {testSets.length === 0 ? (
          <div className="text-center py-12">
            <div className="mx-auto w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mb-4">
              <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No test sets yet</h3>
            <p className="text-gray-500 mb-6">
              Create your first test set to start evaluating prompt performance.
            </p>
            <button
              onClick={() => setShowCreateForm(true)}
              className="bg-celadon-600 text-white px-6 py-3 rounded-lg hover:bg-celadon-700 transition-colors"
            >
              Create Test Set
            </button>
          </div>
        ) : (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {testSets.map((testSet) => (
              <div
                key={testSet.id}
                className="bg-white rounded-lg shadow-sm border hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => navigate(`/projects/${projectId}/test-sets/${testSet.id}`)}
              >
                <div className="p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    {testSet.name}
                  </h3>
                  {testSet.description && (
                    <p className="text-gray-600 text-sm mb-4">
                      {testSet.description}
                    </p>
                  )}
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-gray-500">
                      {testSet.test_case_count} test cases
                    </div>
                    <div className="text-sm text-gray-400">
                      {new Date(testSet.created_at).toLocaleDateString()}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create Test Set Modal */}
      {showCreateForm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-lg bg-white rounded-lg shadow-lg">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Create New Test Set
              </h3>
              
              <form onSubmit={handleCreateTestSet} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Name *
                  </label>
                  <input
                    type="text"
                    value={newTestSet.name}
                    onChange={(e) => setNewTestSet({ ...newTestSet, name: e.target.value })}
                    placeholder="e.g., Customer Support Responses"
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-celadon-500"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    value={newTestSet.description}
                    onChange={(e) => setNewTestSet({ ...newTestSet, description: e.target.value })}
                    placeholder="Describe what this test set evaluates..."
                    rows={3}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-celadon-500"
                  />
                </div>
                
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowCreateForm(false)}
                    className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-celadon-600 text-white rounded-md hover:bg-celadon-700"
                  >
                    Create Test Set
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TestSets; 